import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, ClipboardList, Loader2, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function StudentRoutine() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [routine, setRoutine] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchRoutine();
  }, [navigate]);

  const fetchRoutine = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const response = await fetch(`${API_URL}/api/student-portal/routine`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoutine(data.routine || []);
      }
    } catch (error) {
      console.error('Error fetching routine:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Group routine by day
  const routineByDay = DAYS.reduce((acc, day) => {
    acc[day] = routine.filter(r => r.day === day).sort((a, b) => {
      const timeA = a.start_time || '00:00';
      const timeB = b.start_time || '00:00';
      return timeA.localeCompare(timeB);
    });
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-semibold">Class Routine</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {routine.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <ClipboardList className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Routine Found</h3>
              <p className="text-gray-500">Your class routine will appear here once published.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {DAYS.map(day => (
              <Card key={day}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{day}</CardTitle>
                </CardHeader>
                <CardContent>
                  {routineByDay[day]?.length === 0 ? (
                    <p className="text-gray-500 text-sm py-2">No classes</p>
                  ) : (
                    <div className="space-y-2">
                      {routineByDay[day]?.map((period, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 text-gray-500 w-32">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">
                              {period.start_time} - {period.end_time}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{period.subject}</p>
                            <p className="text-sm text-gray-500">{period.teacher}</p>
                          </div>
                          <div className="text-sm text-gray-500">
                            {period.room}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
